if (!customElements.get('goodwe-pdp-media')) {
  customElements.define('goodwe-pdp-media', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.epoch = (this.epoch || 0) + 1;
      this.playPending = false;
      this.classList.remove('is-playing');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.canPlay = () => {
        const slide = this.closest('[data-gwp-slide]');
        const active = !slide || slide.dataset.active === 'true';
        return this.isConnected && this.visible && active && !document.hidden && !this.motion.matches && !navigator.connection?.saveData && !this.failed;
      };
      this.syncPlayback = () => {
        if (!this.canPlay()) {
          this.video.pause();
          this.classList.remove('is-playing');
          return;
        }
        if (!this.loaded) {
          const sources = [...this.video.querySelectorAll('source[data-src]')];
          const target = Math.min(1920, Math.ceil(this.clientWidth * Math.min(devicePixelRatio || 1, 2)));
          const ranked = sources.filter(s => Number(s.dataset.width) > 0).sort((a, b) => Number(a.dataset.width) - Number(b.dataset.width));
          const source = ranked.find(s => Number(s.dataset.width) >= target) || ranked.at(-1) || sources[0];
          if (!source) return;
          source.src = source.dataset.src;
          this.video.muted = true;
          this.video.defaultMuted = true;
          this.video.loop = true;
          this.video.playsInline = true;
          this.video.load();
          this.loaded = true;
        }
        if (!this.video.paused || this.playPending) return;
        this.playPending = true;
        const epoch = this.epoch;
        const video = this.video;
        try {
          Promise.resolve(video.play()).catch(() => {
            if (this.epoch === epoch) this.classList.remove('is-playing');
          }).finally(() => {
            if (this.epoch !== epoch) return;
            this.playPending = false;
            if (!this.canPlay()) video.pause();
          });
        } catch {
          this.playPending = false;
          this.classList.remove('is-playing');
        }
      };
      this.video.addEventListener('playing', () => {
        if (this.canPlay()) this.classList.add('is-playing');
        else this.video.pause();
      }, {signal});
      this.video.addEventListener('pause', () => this.classList.remove('is-playing'), {signal});
      this.video.addEventListener('error', () => { if (this.loaded) this.failed = true; this.classList.remove('is-playing'); }, {signal});
      document.addEventListener('visibilitychange', this.syncPlayback, {signal});
      this.motion.addEventListener('change', this.syncPlayback, {signal});
      navigator.connection?.addEventListener('change', this.syncPlayback, {signal});
      this.observe = () => {
        if (!this.isConnected) return;
        this.observer = new IntersectionObserver(entries => {
          const entry = entries[0];
          this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
          this.syncPlayback();
        }, { threshold: [0, 0.15, 0.5] });
        this.observer.observe(this);
      };
      if (document.readyState === 'complete') this.observe();
      else window.addEventListener('load', this.observe, { once: true, signal });
    }
    disconnectedCallback() {
      this.epoch = (this.epoch || 0) + 1;
      this.controller?.abort();
      this.observer?.disconnect();
      this.video?.pause();
      this.controller = null;
      this.visible = false;
      this.playPending = false;
      this.classList.remove('is-playing');
    }
  });
}

if (!customElements.get('goodwe-pdp-carousel')) {
  customElements.define('goodwe-pdp-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('[data-gwp-track]');
      this.slides = [...this.querySelectorAll('[data-gwp-slide]')];
      this.nav = this.querySelector('[data-gwp-nav]');
      this.navButtons = [...this.querySelectorAll('[data-gwp-index]')];
      this.controls = this.querySelector('[data-gwp-controls]');
      this.arrows = [...this.querySelectorAll('[data-gwp-direction]')];
      this.status = this.querySelector('[data-gwp-status]');
      this.index = 0;
      if (!this.track || !this.slides.length) return;
      this.classList.add('is-ready');
      if (this.nav) this.nav.hidden = this.slides.length < 2;
      if (this.controls) this.controls.hidden = this.slides.length < 2;
      this.goTo = index => {
        const target = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
        const box = target.getBoundingClientRect();
        const track = this.track.getBoundingClientRect();
        const delta = this.dataset.mode === 'scenes' ? box.left + box.width / 2 - track.left - track.width / 2 : box.left - track.left;
        this.track.scrollBy({left: delta, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      };
      this.activate = index => {
        const changed = index !== this.index;
        this.index = index;
        this.slides.forEach((slide, i) => {
          slide.dataset.active = String(i === index);
          if (i !== index && slide.contains(document.activeElement)) document.activeElement.blur();
          if ('inert' in slide) slide.inert = i !== index;
          slide.querySelectorAll('goodwe-pdp-media').forEach(media => media.syncPlayback?.());
        });
        this.navButtons.forEach((button, i) => {
          if (i === index) button.setAttribute('aria-current', this.dataset.mode === 'steps' ? 'step' : 'true');
          else button.removeAttribute('aria-current');
        });
        this.arrows.forEach(button => {
          button.disabled = Number(button.dataset.gwpDirection) < 0 ? index === 0 : index === this.slides.length - 1;
        });
        if (this.dataset.mode !== 'scenes') this.track.style.height = `${Math.ceil(this.slides[index].getBoundingClientRect().height)}px`;
        if (changed && this.status) this.status.textContent = `${index + 1} / ${this.slides.length}`;
        if (changed && this.nav) {
          const button = this.navButtons[index];
          const nav = this.nav.getBoundingClientRect();
          const box = button?.getBoundingClientRect();
          if (box && (box.left < nav.left || box.right > nav.right)) this.nav.scrollBy({left: box.left - nav.left, behavior: 'instant'});
        }
      };
      this.measure = () => {
        const track = this.track.getBoundingClientRect();
        const center = track.left + track.width / 2;
        let nearest = 0;
        let distance = Infinity;
        this.slides.forEach((slide, i) => {
          const box = slide.getBoundingClientRect();
          const delta = Math.abs(box.left + box.width / 2 - center);
          if (delta < distance) { distance = delta; nearest = i; }
        });
        this.activate(nearest);
      };
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.measure(); });
      };
      this.navButtons.forEach(button => button.addEventListener('click', () => this.goTo(Number(button.dataset.gwpIndex)), {signal}));
      this.arrows.forEach(button => button.addEventListener('click', () => this.goTo(this.index + Number(button.dataset.gwpDirection)), {signal}));
      this.track.addEventListener('scroll', this.onScroll, {passive: true, signal});
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        if (event.key === 'Home') this.goTo(0);
        else if (event.key === 'End') this.goTo(this.slides.length - 1);
        else this.goTo(this.index + ((event.key === 'ArrowRight') !== rtl ? 1 : -1));
      }, {signal});
      this.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.gwpBlock === event.detail?.blockId || slide === event.target || slide.contains(event.target));
        if (index >= 0) this.goTo(index);
      }, {signal});
      this.resize = new ResizeObserver(this.onScroll);
      this.resize.observe(this.track);
      this.slides.forEach(slide => this.resize.observe(slide));
      this.activate(0);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.slides?.forEach(slide => { slide.dataset.active = 'false'; slide.inert = false; slide.querySelectorAll('video').forEach(video => video.pause()); });
      this.controller = null;
      this.frame = null;
    }
  });
}
